import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const AuthContext = createContext();

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// ==================== K3/K4 Security Enhancement ====================
// Store CSRF token in memory (not localStorage) for better XSS protection
let csrfToken = null;

const setCsrfToken = (token) => {
  csrfToken = token;
  if (token) {
    axios.defaults.headers.common['X-CSRF-Token'] = token;
  } else {
    delete axios.defaults.headers.common['X-CSRF-Token'];
  }
};

const getCsrfToken = () => csrfToken;
// ==================== END K3/K4 Security ====================

// ==================== Session-Persistenz (Sliding Session) ====================
// Token liegt IMMER in localStorage: überlebt Browser-Kills auf Mobilgeräten.
// Sliding Refresh: aktive Nutzer verlängern ihre Session automatisch (max. 1x/24h).
const SLIDE_REFRESH_INTERVAL_MS = 24 * 60 * 60 * 1000;

const readStoredToken = () =>
  localStorage.getItem('token') || sessionStorage.getItem('token');

const persistToken = (token) => {
  localStorage.setItem('token', token);
  sessionStorage.setItem('token', token);
  localStorage.setItem('token_refreshed_at', String(Date.now()));
};

const clearStoredAuth = () => {
  sessionStorage.removeItem('token');
  localStorage.removeItem('token');
  localStorage.removeItem('token_refreshed_at');
  localStorage.removeItem('auth_user');
};

const readCachedUser = () => {
  try {
    const raw = localStorage.getItem('auth_user');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const cacheUser = (userData) => {
  try {
    localStorage.setItem('auth_user', JSON.stringify(userData));
  } catch {
    // Storage voll: nicht kritisch
  }
};

// Single-Flight Token-Refresh: parallele 401s lösen genau EINEN Refresh aus
let refreshInFlight = null;
const refreshSessionToken = () => {
  if (!refreshInFlight) {
    const stored = readStoredToken();
    if (!stored) return Promise.reject(new Error('Kein Token vorhanden'));
    refreshInFlight = axios
      .post(`${API_URL}/auth/refresh-token`, null, {
        headers: { Authorization: `Bearer ${stored}` },
      })
      .then(({ data }) => {
        persistToken(data.token);
        if (data.csrf_token) setCsrfToken(data.csrf_token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
        return data.token;
      })
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
};
// ==================== END Session-Persistenz ====================

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const initialToken = readStoredToken();
  // Stale-While-Revalidate: App startet sofort mit gecachtem User, /auth/me validiert im Hintergrund
  const [user, setUser] = useState(initialToken ? readCachedUser() : null);
  const [token, setToken] = useState(initialToken);
  const [loading, setLoading] = useState(initialToken ? !readCachedUser() : false);
  const retryTimerRef = useRef(null);
  const sessionExpiredRef = useRef(false);

  const handleSessionExpired = useCallback((showToast = true) => {
    if (sessionExpiredRef.current) return;
    sessionExpiredRef.current = true;
    clearStoredAuth();
    setCsrfToken(null);
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
    if (showToast) {
      toast.error('Sitzung abgelaufen – bitte melden Sie sich erneut an.');
    }
    setTimeout(() => {
      sessionExpiredRef.current = false;
    }, 3000);
  }, []);

  // Refresh CSRF token periodically (K4 Security)
  const refreshCsrfToken = useCallback(async () => {
    if (readStoredToken()) {
      try {
        const response = await axios.get(`${API_URL}/auth/csrf-token`);
        setCsrfToken(response.data.csrf_token);
      } catch (error) {
        // Non-critical: silently handled
      }
    }
  }, []);

  // Sliding Session: Token verlängern, wenn letzter Refresh > 24h her ist
  const maybeSlideSession = useCallback(async () => {
    const last = Number(localStorage.getItem('token_refreshed_at') || 0);
    if (Date.now() - last < SLIDE_REFRESH_INTERVAL_MS) return;
    try {
      const newToken = await refreshSessionToken();
      setToken(newToken);
    } catch {
      // Non-critical: Interceptor/fetchUser behandeln echte 401s
    }
  }, []);

  // Resiliente Boot-Validierung: logout NUR bei echtem 401/403.
  // Netzwerk-/Serverfehler (Handy-Radio noch offline, 5xx) => Token behalten + Retry mit Backoff.
  const fetchUser = useCallback(async (attempt = 0) => {
    try {
      const response = await axios.get(`${API_URL}/auth/me`);
      setUser(response.data);
      cacheUser(response.data);
      setLoading(false);
      refreshCsrfToken();
      maybeSlideSession();
    } catch (error) {
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        handleSessionExpired(Boolean(readCachedUser()));
        setLoading(false);
        return;
      }
      // Transienter Fehler: mit gecachtem User weiterarbeiten, im Hintergrund erneut versuchen
      if (readCachedUser() || attempt >= 2) {
        setLoading(false);
      }
      const delays = [2000, 5000, 10000, 30000];
      const delay = delays[Math.min(attempt, delays.length - 1)];
      retryTimerRef.current = setTimeout(() => fetchUser(attempt + 1), delay);
    }
  }, [handleSessionExpired, maybeSlideSession, refreshCsrfToken]);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      // Re-Seed nach Mobile-Prozess-Kill: sessionStorage wieder befüllen (ohne Refresh-Timestamp zu ändern)
      if (!sessionStorage.getItem('token')) {
        sessionStorage.setItem('token', token);
      }
      fetchUser();
    } else {
      setLoading(false);
    }
    return () => {
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    };
  }, [token]); // fetchUser bewusst nicht in deps: läuft nur bei Token-Wechsel

  // Globaler 401-Interceptor: einmaliger Silent-Refresh + Request-Retry, sonst sauberer Logout
  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const config = error?.config;
        const status = error?.response?.status;
        const url = config?.url || '';
        const isAuthPath = ['/auth/login', '/auth/refresh-token', '/auth/register', '/auth/qr-login', '/auth/logout', '/auth/me'].some((p) => url.includes(p));
        if (status !== 401 || !config || config._authRetry || isAuthPath || !readStoredToken()) {
          return Promise.reject(error);
        }
        try {
          const newToken = await refreshSessionToken();
          setToken(newToken);
          config._authRetry = true;
          config.headers = { ...(config.headers || {}), Authorization: `Bearer ${newToken}` };
          return axios(config);
        } catch (refreshError) {
          const refreshStatus = refreshError?.response?.status;
          if (refreshStatus === 401 || refreshStatus === 403) {
            handleSessionExpired(true);
          }
          return Promise.reject(error);
        }
      }
    );
    return () => axios.interceptors.response.eject(interceptorId);
  }, [handleSessionExpired]);

  // Sliding Refresh bei App-Rückkehr (Handy entsperrt) + Langläufer-Intervall
  useEffect(() => {
    if (!token) return;
    const onVisible = () => {
      if (document.visibilityState === 'visible') maybeSlideSession();
    };
    document.addEventListener('visibilitychange', onVisible);
    const intervalId = setInterval(maybeSlideSession, 6 * 60 * 60 * 1000);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      clearInterval(intervalId);
    };
  }, [token, maybeSlideSession]);

  // Refresh CSRF token every 30 minutes
  useEffect(() => {
    if (token) {
      const interval = setInterval(refreshCsrfToken, 30 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [token, refreshCsrfToken]);

  const login = async (username, password, rememberMe = false, mfaCode = null, trustDevice = false, trustToken = null) => {
    const response = await axios.post(`${API_URL}/auth/login`, {
      username,
      password,
      remember_me: rememberMe,
      mfa_code: mfaCode,
      trust_device: trustDevice,
      trust_token: trustToken || localStorage.getItem('mfa_trust_token'),
    });

    // Check if MFA is required
    if (response.data.mfa_required) {
      return {
        mfa_required: true,
        user_id: response.data.user_id,
        username: response.data.username,
        message: response.data.message
      };
    }

    // Check if MFA setup is required (haendler/instandhaltung first login)
    if (response.data.mfa_setup_required) {
      return {
        mfa_setup_required: true,
        user_id: response.data.user_id,
        username: response.data.username,
        temp_token: response.data.temp_token,
        message: response.data.message
      };
    }

    const { token: newToken, csrf_token: newCsrfToken, user: userData, trust_token: newTrustToken, mfa_warning } = response.data;

    // Token IMMER persistent speichern (überlebt Browser-Kill auf Mobilgeräten)
    persistToken(newToken);
    cacheUser(userData);
    if (rememberMe) {
      localStorage.setItem('savedUsername', username);
    }

    // Store trust token for MFA bypass
    if (newTrustToken) {
      localStorage.setItem('mfa_trust_token', newTrustToken);
    }

    // K4: Store CSRF token in memory
    setCsrfToken(newCsrfToken);

    setToken(newToken);
    setUser(userData);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;

    // Return user data with MFA warning if present
    return { ...userData, mfa_warning };
  };

  const register = async (username, password, firstName, lastName, email) => {
    const response = await axios.post(`${API_URL}/auth/register`, {
      username,
      password,
      first_name: firstName,
      last_name: lastName,
      email,
    });

    if (response.data.pending) {
      return response.data;
    }

    const { token: newToken, user: userData } = response.data;
    persistToken(newToken);
    cacheUser(userData);
    setToken(newToken);
    setUser(userData);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    return userData;
  };

  const logout = async () => {
    const storedToken = readStoredToken();

    // Lokal sofort abmelden (instant UX), Backend best-effort benachrichtigen
    clearStoredAuth();
    setCsrfToken(null);
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];

    if (storedToken) {
      axios.post(`${API_URL}/auth/logout`, null, {
        headers: { Authorization: `Bearer ${storedToken}` },
      }).catch(() => {
        // Offline/Fehler: lokaler Logout ist bereits erfolgt
      });
    }
  };

  return (
    <AuthContext.Provider value={{ user, token, login, register, logout, loading, getCsrfToken }}>
      {children}
    </AuthContext.Provider>
  );
};

// Export for use in other components if needed
export { getCsrfToken };
